import React from "react";
import { emptyCostItem, summarizeCosts, toMonthlyArs } from "@/domain/projection/engine";
import { formatArs } from "@/domain/projection/format";
import type { CostItem, Currency, CostFrequency, CostKind, ProjectionModel } from "@/domain/projection/types";
import { Button } from "@/presentation/components/common";
import {
  ProjectionNumberField,
  projectionFieldClass,
  projectionSelectClass,
} from "./ProjectionNumberField";

type CostsEditorProps = {
  model: ProjectionModel;
  onChange: (model: ProjectionModel) => void;
};

const CURRENCIES: Currency[] = ["ARS", "USD"];
const FREQUENCIES: Array<{ value: CostFrequency; label: string }> = [
  { value: "monthly", label: "Mensual" },
  { value: "annual", label: "Anual" },
];
const KINDS: Array<{ value: CostKind; label: string }> = [
  { value: "operating", label: "Operativo" },
  { value: "debt", label: "Deuda temporal" },
];

export const CostsEditor: React.FC<CostsEditorProps> = ({ model, onChange }) => {
  const breakdown = summarizeCosts(model, model.adoption.includeDebt);
  const updateItem = (id: string, patch: Partial<CostItem>) => {
    onChange({
      ...model,
      costs: model.costs.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    });
  };

  return (
    <div className="space-y-4">
      <div className="panel-card p-4">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Supuestos globales</h3>
        <p className="mt-1 text-xs text-slate-500">
          Un solo tipo de cambio alimenta costos en USD, Meta y SendGrid. El Excel mezclaba 1500 en Costos con
          1800 en el Resumen; acá no hay valores hardcodeados.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <ProjectionNumberField
            label="Dólar ARS / USD"
            value={model.assumptions.usdArs}
            onChange={(usdArs) => onChange({ ...model, assumptions: { ...model.assumptions, usdArs } })}
            min={1}
            step={1}
          />
          <ProjectionNumberField
            label="Meta USD / WhatsApp"
            value={model.assumptions.metaUsdPerWhatsapp}
            onChange={(metaUsdPerWhatsapp) =>
              onChange({ ...model, assumptions: { ...model.assumptions, metaUsdPerWhatsapp } })
            }
            min={0}
            step={0.001}
          />
          <ProjectionNumberField
            label="SendGrid emails / mes"
            value={model.assumptions.sendgridEmailsPerMonth}
            onChange={(sendgridEmailsPerMonth) =>
              onChange({ ...model, assumptions: { ...model.assumptions, sendgridEmailsPerMonth } })
            }
            min={1}
            step={1000}
          />
          <ProjectionNumberField
            label="SendGrid USD / mes"
            value={model.assumptions.sendgridUsdPerMonth}
            onChange={(sendgridUsdPerMonth) =>
              onChange({ ...model, assumptions: { ...model.assumptions, sendgridUsdPerMonth } })
            }
            min={0}
            step={1}
          />
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Costo unitario WA: {formatArs(model.assumptions.usdArs * model.assumptions.metaUsdPerWhatsapp, true)} ·
          Email:{" "}
          {formatArs(
            model.assumptions.sendgridEmailsPerMonth > 0
              ? (model.assumptions.sendgridUsdPerMonth * model.assumptions.usdArs) /
                  model.assumptions.sendgridEmailsPerMonth
              : 0,
            true,
          )}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onChange({ ...model, assumptions: { ...model.assumptions, usdArs: 1500 } })}
          >
            Dólar 1500 (hoja Costos)
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onChange({ ...model, assumptions: { ...model.assumptions, usdArs: 1800 } })}
          >
            Dólar 1800 (Resumen Excel)
          </Button>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-3">
          <div className="rounded-xl border border-slate-200 px-3 py-2 dark:border-gray-700">
            <p className="text-[11px] uppercase tracking-wide text-slate-400">Operativos</p>
            <p className="text-sm font-semibold tabular-nums">{formatArs(breakdown.operating)}</p>
          </div>
          <div className="rounded-xl border border-slate-200 px-3 py-2 dark:border-gray-700">
            <p className="text-[11px] uppercase tracking-wide text-slate-400">Deuda temporal</p>
            <p className="text-sm font-semibold tabular-nums">{formatArs(breakdown.debt)}</p>
          </div>
          <div className="rounded-xl border border-slate-200 px-3 py-2 dark:border-gray-700">
            <p className="text-[11px] uppercase tracking-wide text-slate-400">Incluidos ahora</p>
            <p className="text-sm font-semibold tabular-nums">{formatArs(breakdown.included)}</p>
          </div>
        </div>
      </div>

      <div className="panel-table-wrap">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 dark:border-gray-700">
          <div>
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Catálogo de costos</h3>
            <p className="text-xs text-slate-500">Alta, baja y edición. Anuales se prorratean a 12 meses.</p>
          </div>
          <Button
            size="sm"
            onClick={() => onChange({ ...model, costs: [...model.costs, emptyCostItem()] })}
          >
            Agregar costo
          </Button>
        </div>
        <div className="panel-table-scroll-hint">Deslizá horizontalmente para ver todas las columnas.</div>
        <table className="min-w-full text-left text-sm">
          <thead className="panel-thead text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Activo</th>
              <th className="px-3 py-2">Concepto</th>
              <th className="px-3 py-2">Importe</th>
              <th className="px-3 py-2">Moneda</th>
              <th className="px-3 py-2">Frecuencia</th>
              <th className="px-3 py-2">Categoría</th>
              <th className="px-3 py-2">Tipo</th>
              <th className="px-3 py-2 text-right">Mensual ARS</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {model.costs.map((item) => (
              <tr key={item.id} className="border-t border-slate-100 dark:border-gray-700">
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={item.enabled}
                    onChange={(event) => updateItem(item.id, { enabled: event.target.checked })}
                  />
                </td>
                <td className="px-3 py-2 min-w-[12rem]">
                  <input
                    className={projectionFieldClass}
                    value={item.name}
                    onChange={(event) => updateItem(item.id, { name: event.target.value })}
                  />
                </td>
                <td className="px-3 py-2 w-32">
                  <input
                    type="number"
                    className={projectionFieldClass}
                    value={item.amount}
                    onChange={(event) => updateItem(item.id, { amount: Number(event.target.value) || 0 })}
                  />
                </td>
                <td className="px-3 py-2 w-24">
                  <select
                    className={projectionSelectClass}
                    value={item.currency}
                    onChange={(event) => updateItem(item.id, { currency: event.target.value as Currency })}
                  >
                    {CURRENCIES.map((currency) => (
                      <option key={currency} value={currency}>{currency}</option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2 w-32">
                  <select
                    className={projectionSelectClass}
                    value={item.frequency}
                    onChange={(event) => updateItem(item.id, { frequency: event.target.value as CostFrequency })}
                  >
                    {FREQUENCIES.map((frequency) => (
                      <option key={frequency.value} value={frequency.value}>{frequency.label}</option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2 w-36">
                  <input
                    className={projectionFieldClass}
                    value={item.category}
                    onChange={(event) => updateItem(item.id, { category: event.target.value })}
                  />
                </td>
                <td className="px-3 py-2 w-40">
                  <select
                    className={projectionSelectClass}
                    value={item.kind}
                    onChange={(event) => updateItem(item.id, { kind: event.target.value as CostKind })}
                  >
                    {KINDS.map((kind) => (
                      <option key={kind.value} value={kind.value}>{kind.label}</option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">
                  {formatArs(toMonthlyArs(item, model.assumptions.usdArs))}
                </td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    className="text-xs font-medium text-rose-600 hover:text-rose-700"
                    onClick={() => onChange({ ...model, costs: model.costs.filter((row) => row.id !== item.id) })}
                  >
                    Quitar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
